/**
 * Detail Panel Controller
 * Orchestrates the detail panel and routes between different views
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { OfferGridView } from "../views/offers/offer-grid-view";
import { OfferDetailView } from "../views/offers/offer-detail-view";
import { BrandListView } from "../views/brands/brand-list-view";
import { BrandOffersView } from "../views/brands/brand-offers-view";
import { CategoryGridView } from "../views/categories/category-grid-view";
import { ProductGridView } from "../views/products/product-grid-view";
import { RedemptionView } from "../views/redemption/redemption-view";
import { getChevronLeftIcon, getCloseIcon, getHeartIcon, getHeartFilledIcon, } from "../views/shared/icons";
import { BottomSheet } from "../views/components/bottom-sheet";
export class DetailPanelController {
    constructor(config, offerService, brandService, redemptionService, onClose) {
        this.config = config;
        this.offerService = offerService;
        this.brandService = brandService;
        this.redemptionService = redemptionService;
        this.onClose = onClose;
        this.isVisible = false;
        this.currentView = null;
        this.viewStack = [];
        this.sessionId = "";
        this.currentAbortController = null;
        // Current state
        this.currentOfferDetail = null;
        this.selectedVariant = null;
        this.currentBrandsWithOffers = [];
        // Redemption state
        this.userBalances = [];
        this.selectedReward = null;
        this.swapAmount = null;
        // Components
        this.bottomSheet = null;
        // Initialize views
        this.offerGridView = new OfferGridView();
        this.offerDetailView = new OfferDetailView();
        this.brandListView = new BrandListView();
        this.brandOffersView = new BrandOffersView();
        this.categoryGridView = new CategoryGridView();
        this.productGridView = new ProductGridView();
        this.redemptionView = new RedemptionView();
        // Create DOM elements
        this.wrapper = document.createElement("div");
        this.wrapper.className = "me-agent-detail-panel-wrapper";
        this.container = document.createElement("div");
        this.container.className = "me-agent-detail-panel";
        // Create fixed header
        this.header = document.createElement("div");
        this.header.className = "me-agent-detail-header";
        // Back button with title
        this.backButton = document.createElement("button");
        this.backButton.className = "me-agent-back-button";
        this.backButton.style.display = "none"; // Hidden by default
        this.backButton.addEventListener("click", () => this.goBack());
        // Title (standalone when no back)
        this.headerTitle = document.createElement("h2");
        this.headerTitle.className = "me-agent-detail-title";
        this.headerTitle.textContent = "Details";
        this.headerTitle.style.display = "block"; // Visible by default
        // Close button
        this.closeButton = document.createElement("button");
        this.closeButton.className = "me-agent-close-button";
        this.closeButton.innerHTML = getCloseIcon({ width: 20, height: 20 });
        this.closeButton.addEventListener("click", () => this.onClose());
        this.header.appendChild(this.backButton);
        this.header.appendChild(this.headerTitle);
        this.header.appendChild(this.closeButton);
        // Create content area
        this.content = document.createElement("div");
        this.content.className = "me-agent-detail-content";
        // Assemble
        this.container.appendChild(this.header);
        this.container.appendChild(this.content);
        this.wrapper.appendChild(this.container);
        // Initialize bottom sheet
        this.bottomSheet = new BottomSheet(this.wrapper);
    }
    /**
     * Get the wrapper element
     */
    getElement() {
        return this.wrapper;
    }
    /**
     * Show the detail panel
     */
    show() {
        this.isVisible = true;
        this.container.classList.add("visible");
    }
    /**
     * Hide the detail panel
     */
    hide() {
        this.cancelCurrentRequest();
        this.isVisible = false;
        this.container.classList.remove("visible");
        this.viewStack = [];
        this.currentView = null;
        // Reset header to default state
        this.backButton.style.display = "none";
        this.headerTitle.style.display = "block";
        this.headerTitle.textContent = "Details";
    }
    /**
     * Cancel any pending requests
     */
    cancelCurrentRequest() {
        if (this.currentAbortController) {
            this.currentAbortController.abort();
            this.currentAbortController = null;
        }
    }
    /**
     * Update header title and back button visibility
     */
    updateHeader(title) {
        // Show back button with title if there's more than one view in the stack
        if (this.viewStack.length > 1) {
            this.backButton.style.display = "flex";
            this.backButton.innerHTML = `
        ${getChevronLeftIcon({ width: 20, height: 20 })}
        <span style="margin-left: 8px;">${title}</span>
      `;
            this.headerTitle.style.display = "none";
        }
        else {
            // Just show title, no back button
            this.backButton.style.display = "none";
            this.headerTitle.style.display = "block";
            this.headerTitle.textContent = title;
        }
    }
    /**
     * Go back to previous view
     */
    goBack() {
        if (this.viewStack.length <= 1) {
            return; // No previous view
        }
        // Cancel any pending requests
        this.cancelCurrentRequest();
        // Pop current view
        this.viewStack.pop();
        // Get previous view state
        const previousView = this.viewStack[this.viewStack.length - 1];
        // Restore previous view
        this.restoreView(previousView);
    }
    /**
     * Restore a view from state
     */
    restoreView(viewState) {
        switch (viewState.type) {
            case "offer-grid":
                this.content.innerHTML = this.offerGridView.render(viewState.data);
                this.attachOfferGridListeners();
                break;
            case "brand-list":
                const origin = window.location.origin;
                this.content.innerHTML = this.brandListView.render(viewState.data, origin);
                this.attachBrandListListeners();
                break;
            case "product-grid":
                this.content.innerHTML = this.productGridView.render(viewState.data);
                break;
            case "category-grid":
                this.content.innerHTML = this.categoryGridView.render(viewState.data);
                this.attachCategoryGridListeners();
                break;
            case "search-category-grid":
                this.content.innerHTML = this.categoryGridView.render(viewState.data);
                this.attachSearchCategoryGridListeners(viewState.data);
                break;
            case "brand-offers":
                this.currentBrandsWithOffers = viewState.data;
                this.content.innerHTML = this.brandOffersView.render(viewState.data);
                this.attachBrandOffersListeners();
                break;
            case "single-brand-offers":
                this.content.innerHTML = this.brandOffersView.renderBrandOffersGrid(viewState.data.offers);
                this.attachSingleBrandOffersListeners();
                break;
            case "offer-detail":
                // Re-fetch offer detail as it needs dynamic data
                const { offerCode, sessionId } = viewState.data;
                this.showOfferDetail(offerCode, sessionId);
                return; // Avoid duplicate header update
        }
        this.currentView = viewState.type;
        this.updateHeader(viewState.title);
    }
    /**
     * Show offer grid
     */
    showOfferGrid(offers, sessionId) {
        this.sessionId = sessionId;
        this.content.innerHTML = this.offerGridView.render(offers);
        this.currentView = "offer-grid";
        this.viewStack = [
            {
                type: "offer-grid",
                title: "Available Offers",
                data: offers,
            },
        ];
        this.updateHeader("Available Offers");
        this.attachOfferGridListeners();
        this.show();
    }
    /**
     * Show offer detail
     */
    showOfferDetail(offerCode, sessionId) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c;
            // Cancel any existing request
            this.cancelCurrentRequest();
            // Create new AbortController
            this.currentAbortController = new AbortController();
            const signal = this.currentAbortController.signal;
            try {
                // Show loading with cancel button
                this.content.innerHTML = this.offerGridView.renderLoading(true);
                this.attachCancelLoadingListener();
                this.updateHeader("Loading...");
                this.show();
                // Fetch offer details
                const detail = yield this.offerService.getOfferDetail(offerCode, sessionId);
                // Check if request was aborted
                if (signal.aborted) {
                    return;
                }
                this.currentOfferDetail = detail;
                this.selectedVariant = ((_a = detail.offerVariants) === null || _a === void 0 ? void 0 : _a[0]) || null;
                // Check if offer is in cart
                const isInCart = this.isOfferInCart(detail.offerCode, (_c = (_b = this.selectedVariant) === null || _b === void 0 ? void 0 : _b.variant) === null || _c === void 0 ? void 0 : _c.id);
                // Render offer detail
                this.content.innerHTML = this.offerDetailView.render(detail, this.selectedVariant, this.config, isInCart);
                this.currentView = "offer-detail";
                this.viewStack.push({
                    type: "offer-detail",
                    title: "Product Details",
                    data: { offerCode, sessionId },
                });
                this.updateHeader("Product Details");
                this.attachOfferDetailListeners();
                // Clear abort controller after successful completion
                this.currentAbortController = null;
            }
            catch (error) {
                // Don't show error if request was aborted
                if (signal.aborted) {
                    return;
                }
                console.error("Error showing offer detail:", error);
                this.content.innerHTML = `<div class="me-agent-error">Failed to load offer details</div>`;
                this.updateHeader("Error");
                this.currentAbortController = null;
            }
        });
    }
    /**
     * Show brand list
     */
    showBrandList(brands) {
        const origin = window.location.origin;
        this.content.innerHTML = this.brandListView.render(brands, origin);
        this.currentView = "brand-list";
        this.viewStack = [
            {
                type: "brand-list",
                title: "Brands with Sign-Up Rewards",
                data: brands,
            },
        ];
        this.updateHeader("Brands with Sign-Up Rewards");
        this.attachBrandListListeners();
        this.show();
    }
    /**
     * Show product grid
     */
    showProductGrid(products) {
        this.content.innerHTML = this.productGridView.render(products);
        this.currentView = "product-grid";
        this.viewStack = [
            {
                type: "product-grid",
                title: "Products",
                data: products,
            },
        ];
        this.updateHeader("Products");
        this.show();
    }
    /**
     * Show category grid
     */
    showCategoryGrid(categories) {
        this.content.innerHTML = this.categoryGridView.render(categories);
        this.currentView = "category-grid";
        this.viewStack = [
            {
                type: "category-grid",
                title: "Purchase Earning Categories",
                data: categories,
            },
        ];
        this.updateHeader("Purchase Earning Categories");
        this.attachCategoryGridListeners();
        this.show();
    }
    /**
     * Show search category grid (for get_categories response)
     * When clicked, fetches and shows offers for that category
     */
    showSearchCategoryGrid(categories, sessionId) {
        this.sessionId = sessionId;
        this.content.innerHTML = this.categoryGridView.render(categories);
        this.currentView = "search-category-grid";
        this.viewStack = [
            {
                type: "search-category-grid",
                title: "Search Categories",
                data: categories,
            },
        ];
        this.updateHeader("Search Categories");
        this.attachSearchCategoryGridListeners(categories);
        this.show();
    }
    /**
     * Show offers for a category (fetch by category ID)
     */
    showOffersForCategory(categoryId, categoryName, token) {
        return __awaiter(this, void 0, void 0, function* () {
            // Cancel any existing request
            this.cancelCurrentRequest();
            // Create new AbortController
            this.currentAbortController = new AbortController();
            const signal = this.currentAbortController.signal;
            try {
                // Show loading with cancel button
                this.content.innerHTML = this.offerGridView.renderLoading(true);
                this.attachCancelLoadingListener();
                this.updateHeader("Loading...");
                this.show();
                console.log("[DetailPanelController] Fetching offers for category:", categoryId);
                // Fetch offers for this category
                const offers = yield this.offerService.getOffersByCategoryId(categoryId, token);
                console.log("[DetailPanelController] Fetched offers:", offers.length);
                // Check if request was aborted
                if (signal.aborted) {
                    console.log("[DetailPanelController] Request was aborted");
                    return;
                }
                // Check if we have offers
                if (!offers || offers.length === 0) {
                    this.content.innerHTML = `<div class="me-agent-error">No offers found in this category</div>`;
                    this.updateHeader(categoryName);
                    this.currentAbortController = null;
                    return;
                }
                // Render offers in a grid
                console.log("[DetailPanelController] Rendering offers grid");
                this.content.innerHTML = this.offerGridView.render(offers);
                this.currentView = "offer-grid";
                this.viewStack.push({
                    type: "offer-grid",
                    title: categoryName,
                    data: offers,
                });
                this.updateHeader(categoryName);
                this.attachOfferGridListeners();
                // Clear abort controller after successful completion
                this.currentAbortController = null;
            }
            catch (error) {
                // Don't show error if request was aborted
                if (signal.aborted) {
                    return;
                }
                console.error("[DetailPanelController] Error showing offers for category:", error);
                this.content.innerHTML = `<div class="me-agent-error">Failed to load offers. Please try again.</div>`;
                this.updateHeader("Error");
                this.currentAbortController = null;
            }
        });
    }
    /**
     * Show brands with offers for a category
     */
    showBrandsWithOffers(categoryId, token) {
        return __awaiter(this, void 0, void 0, function* () {
            // Cancel any existing request
            this.cancelCurrentRequest();
            // Create new AbortController
            this.currentAbortController = new AbortController();
            const signal = this.currentAbortController.signal;
            try {
                // Show loading with cancel button
                this.content.innerHTML = this.offerGridView.renderLoading(true);
                this.attachCancelLoadingListener();
                this.updateHeader("Loading...");
                this.show();
                // Fetch brands with offers
                const brandsWithOffers = yield this.brandService.getBrandsWithOffers(categoryId, token);
                // Check if request was aborted
                if (signal.aborted) {
                    return;
                }
                // Store and render brands with offers
                this.currentBrandsWithOffers = brandsWithOffers;
                this.content.innerHTML = this.brandOffersView.render(brandsWithOffers);
                this.currentView = "brand-offers";
                this.viewStack.push({
                    type: "brand-offers",
                    title: "Brands with Purchase Rewards",
                    data: brandsWithOffers,
                });
                this.updateHeader("Brands with Purchase Rewards");
                this.attachBrandOffersListeners();
                // Clear abort controller after successful completion
                this.currentAbortController = null;
            }
            catch (error) {
                // Don't show error if request was aborted
                if (signal.aborted) {
                    return;
                }
                console.error("Error showing brands with offers:", error);
                this.content.innerHTML = `<div class="me-agent-error">Failed to load brands</div>`;
                this.updateHeader("Error");
                this.currentAbortController = null;
            }
        });
    }
    /**
     * Attach event listeners for offer grid
     */
    attachOfferGridListeners() {
        // Offer cards (using brand-offer-card class for consistency)
        const offerCards = this.content.querySelectorAll(".me-agent-brand-offer-card");
        offerCards.forEach((card) => {
            card.addEventListener("click", () => {
                const offerCode = card.getAttribute("data-offer-code");
                if (offerCode && this.sessionId) {
                    this.showOfferDetail(offerCode, this.sessionId);
                }
            });
        });
    }
    /**
     * Attach event listeners for offer detail
     */
    attachOfferDetailListeners() {
        // Variant selection
        const variantCards = this.content.querySelectorAll(".me-agent-variant-card");
        variantCards.forEach((card) => {
            card.addEventListener("click", () => {
                const variantId = card.getAttribute("data-variant-id");
                if (variantId && this.currentOfferDetail) {
                    this.selectVariant(variantId);
                }
            });
        });
        // Tabs
        const tabs = this.content.querySelectorAll(".me-agent-tab");
        tabs.forEach((tab) => {
            tab.addEventListener("click", () => {
                const tabName = tab.getAttribute("data-tab");
                if (tabName) {
                    this.switchTab(tabName);
                }
            });
        });
        // Action buttons
        this.attachActionListeners();
    }
    /**
     * Attach event listeners for brand list
     */
    attachBrandListListeners() {
        // No additional listeners needed - back/close handled by header
    }
    /**
     * Attach event listeners for category grid
     */
    attachCategoryGridListeners() {
        // Category cards
        const categoryCards = this.content.querySelectorAll(".me-agent-category-card");
        categoryCards.forEach((card) => {
            card.addEventListener("click", () => {
                const categoryId = card.getAttribute("data-category-id");
                if (categoryId) {
                    this.showBrandsWithOffers(categoryId);
                }
            });
        });
    }
    /**
     * Attach event listeners for search category grid
     * (for get_categories response - fetches offers by category ID)
     */
    attachSearchCategoryGridListeners(categories) {
        const categoryCards = this.content.querySelectorAll(".me-agent-category-card");
        categoryCards.forEach((card) => {
            card.addEventListener("click", () => {
                const categoryId = card.getAttribute("data-category-id");
                const category = categories.find((cat) => cat.categoryId === categoryId);
                if (categoryId && category) {
                    this.showOffersForCategory(categoryId, category.categoryName || category.title || "Category");
                }
            });
        });
    }
    /**
     * Show all offers for a single brand
     */
    showSingleBrandOffers(brandId, brandName) {
        // Find the brand from stored data
        const brandData = this.currentBrandsWithOffers.find((item) => item.brand.id === brandId);
        if (!brandData) {
            console.error("Brand not found:", brandId);
            return;
        }
        // Render all offers for this brand
        this.content.innerHTML = this.brandOffersView.renderBrandOffersGrid(brandData.offers);
        this.currentView = "single-brand-offers";
        this.viewStack.push({
            type: "single-brand-offers",
            title: brandName,
            data: { brandId, brandName, offers: brandData.offers },
        });
        this.updateHeader(brandName);
        this.attachSingleBrandOffersListeners();
    }
    /**
     * Attach event listeners for single brand offers grid
     */
    attachSingleBrandOffersListeners() {
        // Offer cards - navigate to product URL
        const offerCards = this.content.querySelectorAll(".me-agent-brand-offer-card");
        offerCards.forEach((card) => {
            card.addEventListener("click", () => {
                const productUrl = card.getAttribute("data-product-url");
                if (productUrl && productUrl !== "#") {
                    window.open(productUrl, "_blank");
                }
            });
        });
    }
    /**
     * Attach event listeners for brand offers
     */
    attachBrandOffersListeners() {
        // Offer cards - navigate to product URL
        const offerCards = this.content.querySelectorAll(".me-agent-brand-offer-card");
        offerCards.forEach((card) => {
            card.addEventListener("click", () => {
                const productUrl = card.getAttribute("data-product-url");
                if (productUrl && productUrl !== "#") {
                    window.open(productUrl, "_blank");
                }
            });
        });
        // View All buttons
        const viewAllButtons = this.content.querySelectorAll(".me-agent-view-all-offers-btn");
        viewAllButtons.forEach((button) => {
            button.addEventListener("click", () => {
                const brandSection = button.closest(".me-agent-brand-offers-section");
                const brandId = brandSection === null || brandSection === void 0 ? void 0 : brandSection.getAttribute("data-brand-id");
                const brandName = button.getAttribute("data-brand-name") || "Brand";
                if (brandId) {
                    this.showSingleBrandOffers(brandId, brandName);
                }
            });
        });
    }
    /**
     * Attach cancel button listener during loading
     */
    attachCancelLoadingListener() {
        const cancelBtn = this.content.querySelector(".me-agent-cancel-loading-btn");
        if (cancelBtn) {
            cancelBtn.addEventListener("click", () => {
                // Cancel the current request
                this.cancelCurrentRequest();
                // Restore current view (top of stack) without popping
                const currentView = this.viewStack[this.viewStack.length - 1];
                if (currentView) {
                    this.restoreView(currentView);
                }
            });
        }
    }
    /**
     * Check if an offer is in cart
     */
    isOfferInCart(offerCode, variantId) {
        if (!this.config.cartItems)
            return false;
        return this.config.cartItems.some((item) => {
            if (item.offerCode !== offerCode)
                return false;
            // If variantId is specified, check if it matches
            if (variantId) {
                return item.variantId === variantId;
            }
            // If no variantId, just match by offer code
            return true;
        });
    }
    /**
     * Attach action button listeners (like, share, add to cart)
     */
    attachActionListeners() {
        const redeemBtn = this.content.querySelector('[data-action="redeem"]');
        const likeBtn = this.content.querySelector('[data-action="like"]');
        const shareBtn = this.content.querySelector('[data-action="share"]');
        const cartBtn = this.content.querySelector('[data-action="add-to-cart"]');
        // Redeem button
        redeemBtn === null || redeemBtn === void 0 ? void 0 : redeemBtn.addEventListener("click", () => {
            if (this.currentOfferDetail) {
                this.handleRedemption();
            }
        });
        likeBtn === null || likeBtn === void 0 ? void 0 : likeBtn.addEventListener("click", () => {
            if (this.currentOfferDetail && this.config.onLikeUnlike) {
                const isLiked = likeBtn.getAttribute("data-liked") === "true";
                this.config.onLikeUnlike(this.currentOfferDetail, !isLiked);
                likeBtn.setAttribute("data-liked", (!isLiked).toString());
                // Update button icon
                likeBtn.innerHTML = !isLiked ? getHeartFilledIcon() : getHeartIcon();
            }
        });
        shareBtn === null || shareBtn === void 0 ? void 0 : shareBtn.addEventListener("click", () => {
            if (this.currentOfferDetail && this.config.onShare) {
                this.config.onShare(this.currentOfferDetail);
            }
        });
        cartBtn === null || cartBtn === void 0 ? void 0 : cartBtn.addEventListener("click", () => __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            if (!this.currentOfferDetail)
                return;
            const isInCart = cartBtn.getAttribute("data-in-cart") === "true";
            const variantId = (_b = (_a = this.selectedVariant) === null || _a === void 0 ? void 0 : _a.variant) === null || _b === void 0 ? void 0 : _b.id;
            if (isInCart) {
                // Remove from cart
                if (this.config.onRemoveFromCart) {
                    yield this.config.onRemoveFromCart(this.currentOfferDetail.offerCode, variantId);
                    // Update button state
                    cartBtn.setAttribute("data-in-cart", "false");
                    cartBtn.textContent = "Add to Cart";
                }
            }
            else {
                // Add to cart
                if (this.config.onAddToCart) {
                    yield this.config.onAddToCart(this.currentOfferDetail, variantId);
                    // Update button state
                    cartBtn.setAttribute("data-in-cart", "true");
                    cartBtn.textContent = "Remove from Cart";
                }
            }
        }));
    }
    /**
     * Handle redemption button click
     */
    handleRedemption() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.currentOfferDetail)
                return;
            try {
                // Show loading
                this.content.innerHTML = this.redemptionView.renderLoading();
                // Step 1: Check if email is available
                const email = this.redemptionService.getEmail();
                if (!email) {
                    throw new Error("Email is required for redemption. Please configure the SDK with an email address.");
                }
                // Step 2: Ensure Magic is logged in (will trigger OTP if needed)
                yield this.redemptionService.ensureMagicLogin(email);
                // Step 3: Login to ME Protocol
                yield this.redemptionService.loginToMEProtocol();
                // Step 4: Fetch user's reward balances
                this.userBalances = yield this.redemptionService.fetchBalances();
                if (this.userBalances.length === 0) {
                    this.content.innerHTML = `
          <div class="me-agent-error-message">
            <p>You don't have any reward tokens yet.</p>
            <button class="me-agent-try-again-btn" data-action="back">Go Back</button>
          </div>
        `;
                    this.attachBackButtonListener();
                    return;
                }
                // Step 3: Select default reward (offer's reward if available, else first reward)
                this.selectedReward = this.findDefaultReward();
                // Step 4: Show redemption review (with loading state for amount)
                this.swapAmount = {
                    amount: 0,
                    amountNeeded: 0,
                    checkAffordability: false,
                }; // Placeholder
                this.showRedemptionReview();
                // Step 5: Calculate swap amount in background and update UI
                yield this.calculateAndUpdateSwapAmount();
            }
            catch (error) {
                console.error("Redemption error:", error);
                this.content.innerHTML = this.redemptionView.renderError(error instanceof Error ? error.message : "Failed to start redemption");
                this.attachRedemptionRetryListener();
            }
        });
    }
    /**
     * Find default reward to use for redemption
     */
    findDefaultReward() {
        var _a;
        if (!this.currentOfferDetail)
            throw new Error("No offer selected");
        const offerNetwork = (_a = this.currentOfferDetail.brand) === null || _a === void 0 ? void 0 : _a.network;
        // Try to find the offer's exact reward
        const offerReward = this.userBalances.find((r) => r.reward.id === this.currentOfferDetail.reward.id);
        if (offerReward)
            return offerReward;
        // If offer reward not found, prefer rewards in the same network
        if (offerNetwork) {
            const sameNetworkReward = this.userBalances.find((r) => r.reward.brandNetwork === offerNetwork);
            if (sameNetworkReward)
                return sameNetworkReward;
        }
        // Otherwise use first available reward
        return this.userBalances[0];
    }
    /**
     * Calculate swap amount needed for redemption
     */
    calculateSwapAmount() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            if (!this.currentOfferDetail || !this.selectedReward)
                return;
            const variant = this.selectedVariant || ((_a = this.currentOfferDetail.offerVariants) === null || _a === void 0 ? void 0 : _a[0]);
            const variantId = ((_b = variant === null || variant === void 0 ? void 0 : variant.variant) === null || _b === void 0 ? void 0 : _b.id) || (variant === null || variant === void 0 ? void 0 : variant.variantId);
            this.swapAmount = yield this.redemptionService.calculateSwapAmount(this.selectedReward.reward.contractAddress, this.currentOfferDetail, variantId);
        });
    }
    /**
     * Calculate swap amount and update the UI with result or error
     */
    calculateAndUpdateSwapAmount() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.currentOfferDetail || !this.selectedReward)
                return;
            const amountElement = this.content.querySelector(".me-agent-amount-needed");
            const offerAmountElement = this.content.querySelector(".me-agent-offer-amount-needed");
            const redeemButton = this.content.querySelector(".me-agent-redeem-btn");
            if (!amountElement)
                return;
            try {
                // Show loading in both places
                amountElement.innerHTML = `
        <span style="display: flex; align-items: center; gap: 8px;">
          <span style="animation: spin 1s linear infinite;">⏳</span>
          Loading...
        </span>
      `;
                if (offerAmountElement) {
                    offerAmountElement.textContent = "Loading...";
                }
                // Calculate amount
                yield this.calculateSwapAmount();
                // Check if view was changed during calculation
                if (!this.swapAmount ||
                    this.content.querySelector(".me-agent-redemption-container") === null) {
                    return;
                }
                // Determine which amount to display (prioritize amountNeeded for cross-brand)
                const displayAmount = this.swapAmount.amountNeeded || this.swapAmount.amount || 0;
                // Update both amount displays with actual amount
                amountElement.textContent = `${displayAmount.toFixed(2)} ${this.selectedReward.reward.symbol}`;
                amountElement.style.color = "";
                if (offerAmountElement) {
                    offerAmountElement.textContent = `${displayAmount.toFixed(2)} ${this.selectedReward.reward.symbol} Needed`;
                }
                // Enable/disable button based on affordability
                if (redeemButton) {
                    const isAffordable = this.selectedReward.balance >= displayAmount;
                    redeemButton.disabled = !isAffordable;
                    // Show insufficient balance message if needed
                    if (!isAffordable) {
                        const errorMsg = this.content.querySelector(".me-agent-error-message");
                        if (!errorMsg) {
                            const rewardSelection = this.content.querySelector(".me-agent-reward-selection");
                            if (rewardSelection) {
                                const errorDiv = document.createElement("div");
                                errorDiv.className = "me-agent-error-message";
                                errorDiv.innerHTML = `
                <p>Insufficient balance. You need ${displayAmount.toFixed(2)} ${this.selectedReward.reward.symbol} but only have ${this.selectedReward.balance.toFixed(2)}.</p>
              `;
                                rewardSelection.appendChild(errorDiv);
                            }
                        }
                    }
                }
            }
            catch (error) {
                console.error("Error calculating swap amount:", error);
                // Show error in red
                amountElement.innerHTML = `<span style="color: #ef4444;">Failed to fetch needed amount</span>`;
                // Disable redeem button
                if (redeemButton) {
                    redeemButton.disabled = true;
                }
            }
        });
    }
    /**
     * Show redemption review step
     */
    showRedemptionReview() {
        var _a;
        if (!this.currentOfferDetail || !this.selectedReward || !this.swapAmount)
            return;
        const variant = this.selectedVariant || ((_a = this.currentOfferDetail.offerVariants) === null || _a === void 0 ? void 0 : _a[0]);
        this.viewStack.push({
            type: "redemption-review",
            title: "Complete Your Redemption",
            data: {
                offer: this.currentOfferDetail,
                reward: this.selectedReward,
                swapAmount: this.swapAmount,
                variant,
            },
        });
        this.content.innerHTML = this.redemptionView.renderReviewStep(this.currentOfferDetail, this.selectedReward, this.swapAmount, variant);
        this.updateHeader("Complete Your Redemption");
        this.attachRedemptionReviewListeners();
    }
    /**
     * Attach listeners for redemption review step
     */
    attachRedemptionReviewListeners() {
        // Change reward button
        const changeRewardBtn = this.content.querySelector(".me-agent-change-reward-btn");
        changeRewardBtn === null || changeRewardBtn === void 0 ? void 0 : changeRewardBtn.addEventListener("click", () => {
            this.showRewardSelectionList();
        });
        // Redeem button
        const redeemBtn = this.content.querySelector(".me-agent-redeem-btn");
        redeemBtn === null || redeemBtn === void 0 ? void 0 : redeemBtn.addEventListener("click", () => __awaiter(this, void 0, void 0, function* () {
            yield this.executeRedemption();
        }));
    }
    /**
     * Show list of available rewards for selection
     */
    showRewardSelectionList() {
        var _a, _b, _c;
        if (!this.userBalances.length || !this.bottomSheet)
            return;
        // Get offer network for filtering
        const offerNetwork = (_b = (_a = this.currentOfferDetail) === null || _a === void 0 ? void 0 : _a.brand) === null || _b === void 0 ? void 0 : _b.network;
        // Use view to generate HTML
        const listHTML = this.redemptionView.renderRewardList(this.userBalances, ((_c = this.selectedReward) === null || _c === void 0 ? void 0 : _c.reward.id) || "", offerNetwork);
        // Show in bottom sheet
        this.bottomSheet.show("Select a Reward", listHTML);
        // Attach listeners after bottom sheet is shown
        setTimeout(() => {
            var _a;
            const contentElement = (_a = this.bottomSheet) === null || _a === void 0 ? void 0 : _a.getContentElement();
            if (contentElement) {
                const rewardItems = contentElement.querySelectorAll(".me-agent-reward-list-item");
                rewardItems.forEach((item) => {
                    item.addEventListener("click", () => __awaiter(this, void 0, void 0, function* () {
                        // Check if item is disabled
                        const isDisabled = item.getAttribute("data-disabled") === "true";
                        if (isDisabled) {
                            return; // Don't allow selecting disabled rewards
                        }
                        const rewardId = item.getAttribute("data-reward-id");
                        if (rewardId) {
                            yield this.selectReward(rewardId);
                        }
                    }));
                });
            }
        }, 0);
    }
    /**
     * Select a different reward
     */
    selectReward(rewardId) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            const reward = this.userBalances.find((r) => r.reward.id === rewardId);
            if (!reward)
                return;
            // Close bottom sheet
            (_a = this.bottomSheet) === null || _a === void 0 ? void 0 : _a.hide();
            this.selectedReward = reward;
            this.swapAmount = { amount: 0, amountNeeded: 0, checkAffordability: false }; // Reset to loading state
            this.showRedemptionReview();
            yield this.calculateAndUpdateSwapAmount();
        });
    }
    /**
     * Execute the redemption transaction
     */
    executeRedemption() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            if (!this.currentOfferDetail || !this.selectedReward || !this.swapAmount)
                return;
            try {
                // Show processing step
                this.content.innerHTML = this.redemptionView.renderProcessingStep(this.currentOfferDetail);
                this.updateHeader("Processing");
                const variant = this.selectedVariant || ((_a = this.currentOfferDetail.offerVariants) === null || _a === void 0 ? void 0 : _a[0]);
                const variantId = variant === null || variant === void 0 ? void 0 : variant.id;
                // Determine if same-brand or cross-brand redemption
                const isSameBrand = this.selectedReward.reward.contractAddress ===
                    this.currentOfferDetail.reward.contractAddress;
                let order;
                if (isSameBrand) {
                    // Same brand redemption
                    order = yield this.redemptionService.executeSameBrandRedemption(this.selectedReward.reward.contractAddress, this.selectedReward.reward.id, this.swapAmount.amount.toString(), this.currentOfferDetail.id, this.currentOfferDetail.redemptionMethod.id, this.currentOfferDetail.brand.id, variantId);
                }
                else {
                    // Cross brand redemption
                    order = yield this.redemptionService.executeCrossBrandRedemption(this.selectedReward.reward.contractAddress, this.selectedReward.reward.id, this.swapAmount.amount.toString(), this.swapAmount.amountNeeded.toString(), this.currentOfferDetail.reward.contractAddress, this.currentOfferDetail.id, this.currentOfferDetail.redemptionMethod.id, this.currentOfferDetail.brand.id, this.currentOfferDetail.brand.network, variantId);
                }
                // Show complete step
                this.showRedemptionComplete(order);
            }
            catch (error) {
                console.error("Redemption transaction error:", error);
                this.content.innerHTML = this.redemptionView.renderError(error instanceof Error
                    ? error.message
                    : "Redemption failed. Please try again.");
                this.updateHeader("Error");
                this.attachRedemptionRetryListener();
            }
        });
    }
    /**
     * Show redemption complete step
     */
    showRedemptionComplete(order) {
        if (!this.currentOfferDetail)
            return;
        this.content.innerHTML = this.redemptionView.renderCompleteStep(order, this.currentOfferDetail);
        this.updateHeader("Complete");
        this.attachRedemptionCompleteListeners(order);
    }
    /**
     * Attach listeners for redemption complete step
     */
    attachRedemptionCompleteListeners(order) {
        // Copy coupon button
        const copyBtn = this.content.querySelector(".me-agent-copy-coupon-btn");
        copyBtn === null || copyBtn === void 0 ? void 0 : copyBtn.addEventListener("click", () => __awaiter(this, void 0, void 0, function* () {
            const couponCode = copyBtn.getAttribute("data-code");
            if (couponCode) {
                try {
                    yield navigator.clipboard.writeText(couponCode);
                    copyBtn.textContent = "Copied!";
                    setTimeout(() => {
                        copyBtn.textContent = "Copy";
                    }, 2000);
                }
                catch (error) {
                    console.error("Failed to copy:", error);
                    // Fallback for older browsers
                    const textArea = document.createElement("textarea");
                    textArea.value = couponCode;
                    textArea.style.position = "fixed";
                    textArea.style.left = "-999999px";
                    document.body.appendChild(textArea);
                    textArea.select();
                    try {
                        document.execCommand("copy");
                        copyBtn.textContent = "Copied!";
                        setTimeout(() => {
                            copyBtn.textContent = "Copy";
                        }, 2000);
                    }
                    catch (err) {
                        console.error("Fallback copy failed:", err);
                    }
                    document.body.removeChild(textArea);
                }
            }
        }));
        // Use coupon button
        const useCouponBtn = this.content.querySelector(".me-agent-use-coupon-btn");
        useCouponBtn === null || useCouponBtn === void 0 ? void 0 : useCouponBtn.addEventListener("click", () => __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            try {
                if (!this.currentOfferDetail) {
                    alert("Offer details not available.");
                    return;
                }
                // Show loading state
                const originalText = useCouponBtn.textContent;
                useCouponBtn.textContent = "Generating checkout...";
                useCouponBtn.disabled = true;
                // Get the variant that was redeemed
                const variant = this.selectedVariant || ((_a = this.currentOfferDetail.offerVariants) === null || _a === void 0 ? void 0 : _a[0]);
                if (!((_b = variant === null || variant === void 0 ? void 0 : variant.variant) === null || _b === void 0 ? void 0 : _b.variantIdOnBrandSite)) {
                    throw new Error("Product variant ID not available");
                }
                let checkoutUrl = yield this.redemptionService.getCheckoutUrl(this.currentOfferDetail.brand.id, variant.variant.variantIdOnBrandSite);
                // Ensure URL has protocol
                if (!checkoutUrl.startsWith("http://") &&
                    !checkoutUrl.startsWith("https://")) {
                    checkoutUrl = "https://" + checkoutUrl;
                }
                // Open checkout in new tab
                window.open(checkoutUrl, "_blank");
                // Reset button state
                useCouponBtn.textContent = originalText;
                useCouponBtn.disabled = false;
            }
            catch (error) {
                console.error("Error getting checkout URL:", error);
                alert(error instanceof Error
                    ? error.message
                    : "Failed to generate checkout URL. Please use the coupon code manually.");
                // Reset button state
                useCouponBtn.textContent = "Use Coupon";
                useCouponBtn.disabled = false;
            }
        }));
    }
    /**
     * Attach retry listener for redemption errors
     */
    attachRedemptionRetryListener() {
        const retryBtn = this.content.querySelector('[data-action="retry-redemption"]');
        retryBtn === null || retryBtn === void 0 ? void 0 : retryBtn.addEventListener("click", () => {
            // If we already have user balances and selected reward, go back to review step
            // Otherwise, restart the entire redemption flow
            if (this.userBalances.length > 0 &&
                this.selectedReward &&
                this.currentOfferDetail) {
                this.showRedemptionReview();
                this.calculateAndUpdateSwapAmount();
            }
            else {
                this.handleRedemption();
            }
        });
    }
    /**
     * Attach back button listener
     */
    attachBackButtonListener() {
        const backBtn = this.content.querySelector('[data-action="back"]');
        backBtn === null || backBtn === void 0 ? void 0 : backBtn.addEventListener("click", () => {
            this.goBack();
        });
    }
    /**
     * Select a variant
     */
    selectVariant(variantId) {
        var _a, _b;
        if (!this.currentOfferDetail)
            return;
        const variant = (_a = this.currentOfferDetail.offerVariants) === null || _a === void 0 ? void 0 : _a.find((v) => v.id === variantId);
        if (variant) {
            this.selectedVariant = variant;
            // Check if offer is in cart
            const isInCart = this.isOfferInCart(this.currentOfferDetail.offerCode, (_b = variant.variant) === null || _b === void 0 ? void 0 : _b.id);
            // Re-render
            this.content.innerHTML = this.offerDetailView.render(this.currentOfferDetail, this.selectedVariant, this.config, isInCart);
            this.attachOfferDetailListeners();
        }
    }
    /**
     * Switch tab
     */
    switchTab(tabName) {
        // Remove active class from all tabs and panes
        this.content.querySelectorAll(".me-agent-tab").forEach((tab) => {
            tab.classList.remove("active");
        });
        this.content.querySelectorAll(".me-agent-tab-pane").forEach((pane) => {
            pane.classList.remove("active");
        });
        // Add active class to selected tab and pane
        const selectedTab = this.content.querySelector(`[data-tab="${tabName}"]`);
        const selectedPane = this.content.querySelector(`[data-pane="${tabName}"]`);
        selectedTab === null || selectedTab === void 0 ? void 0 : selectedTab.classList.add("active");
        selectedPane === null || selectedPane === void 0 ? void 0 : selectedPane.classList.add("active");
    }
}
